// This file is auto-generated by @hey-api/openapi-ts

import type { Client, Options as Options2, TDataShape } from './client';
import { client } from './client.gen';
import type {
  CheckHealthData,
  CheckHealthErrors,
  CheckHealthResponses,
  CompleteMissionData,
  CompleteMissionErrors,
  CompleteMissionResponses,
  CreateCameraData,
  CreateCameraErrors,
  CreateCameraResponses,
  CreateCamerasData,
  CreateCamerasErrors,
  CreateCamerasResponses,
  CreateMissionData,
  CreateMissionErrors,
  CreateMissionResponses,
  CreateRunData,
  CreateRunErrors,
  CreateRunResponses,
  CreateRunsData,
  CreateRunsErrors,
  CreateRunsResponses,
  CreateUserProjectSettingData,
  CreateUserProjectSettingErrors,
  CreateUserProjectSettingResponses,
  CreateUserSettingData,
  CreateUserSettingErrors,
  CreateUserSettingResponses,
  DeleteCameraData,
  DeleteCameraErrors,
  DeleteCameraResponses,
  DeleteMissionData,
  DeleteMissionErrors,
  DeleteMissionResponses,
  DeleteRunData,
  DeleteRunErrors,
  DeleteRunResponses,
  DeleteUserProjectSettingData,
  DeleteUserProjectSettingErrors,
  DeleteUserProjectSettingResponses,
  DeleteUserSettingData,
  DeleteUserSettingErrors,
  DeleteUserSettingResponses,
  GetCameraData,
  GetCameraErrors,
  GetCameraResponses,
  GetMissionData,
  GetMissionErrors,
  GetMissionResponses,
  GetRunData,
  GetRunErrors,
  GetRunResponses,
  GetUserProjectSettingData,
  GetUserProjectSettingErrors,
  GetUserProjectSettingResponses,
  GetUserSettingData,
  GetUserSettingErrors,
  GetUserSettingResponses,
  ListCamerasData,
  ListCamerasErrors,
  ListCamerasResponses,
  ListMissionsData,
  ListMissionsErrors,
  ListMissionsExpandedData,
  ListMissionsExpandedErrors,
  ListMissionsExpandedResponses,
  ListMissionsResponses,
  ListRunsData,
  ListRunsErrors,
  ListRunsResponses,
  ListUserProjectSettingsData,
  ListUserProjectSettingsErrors,
  ListUserProjectSettingsResponses,
  ListUserSettingsData,
  ListUserSettingsErrors,
  ListUserSettingsResponses,
  PatchMissionData,
  PatchMissionErrors,
  PatchMissionResponses,
  PatchRunData,
  PatchRunErrors,
  PatchRunResponses,
  UpdateUserProjectSettingData,
  UpdateUserProjectSettingErrors,
  UpdateUserProjectSettingResponses,
  UpdateUserSettingData,
  UpdateUserSettingErrors,
  UpdateUserSettingResponses,
} from './types.gen';

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
> = Options2<TData, ThrowOnError> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>;
};

/**
 * Deletes a camera by its ID.
 */
export const deleteCamera = <ThrowOnError extends boolean = false>(
  options: Options<DeleteCameraData, ThrowOnError>
) =>
  (options.client ?? client).delete<
    DeleteCameraResponses,
    DeleteCameraErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions/{missionTrn}/runs/{runId}/cameras/{cameraId}',
    ...options,
  });

/**
 * Returns a specific camera for the given run.
 */
export const getCamera = <ThrowOnError extends boolean = false>(
  options: Options<GetCameraData, ThrowOnError>
) =>
  (options.client ?? client).get<
    GetCameraResponses,
    GetCameraErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions/{missionTrn}/runs/{runId}/cameras/{cameraId}',
    ...options,
  });

/**
 * Lists all cameras for the given mission and run.
 */
export const listCameras = <ThrowOnError extends boolean = false>(
  options: Options<ListCamerasData, ThrowOnError>
) =>
  (options.client ?? client).get<
    ListCamerasResponses,
    ListCamerasErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions/{missionTrn}/runs/{runId}/cameras',
    ...options,
  });

/**
 * Creates a camera for the given mission and run with external dataset creation.
 */
export const createCamera = <ThrowOnError extends boolean = false>(
  options: Options<CreateCameraData, ThrowOnError>
) =>
  (options.client ?? client).post<
    CreateCameraResponses,
    CreateCameraErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions/{missionTrn}/runs/{runId}/cameras',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Creates multiple cameras for the given mission and run with external dataset creation.
 */
export const createCameras = <ThrowOnError extends boolean = false>(
  options: Options<CreateCamerasData, ThrowOnError>
) =>
  (options.client ?? client).post<
    CreateCamerasResponses,
    CreateCamerasErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions/{missionTrn}/runs/{runId}/cameras/bulk',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Check the health of the services
 *
 * ### Check Health
 *
 * This endpoint will check the health of the external services and return the status of each service.
 *
 * ### Notes
 *
 * - This endpoint is does not require authentication.
 * - The region parameter should be one of the supported Trimble Connect regions as listed above.
 */
export const checkHealth = <ThrowOnError extends boolean = false>(
  options: Options<CheckHealthData, ThrowOnError>
) =>
  (options.client ?? client).get<
    CheckHealthResponses,
    CheckHealthErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/health/{region}',
    ...options,
  });

/**
 * Returns all missions for the given tenant.
 */
export const listMissions = <ThrowOnError extends boolean = false>(
  options: Options<ListMissionsData, ThrowOnError>
) =>
  (options.client ?? client).get<
    ListMissionsResponses,
    ListMissionsErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions',
    ...options,
  });

/**
 * Creates a new mission for the tenant with external alignment creation.
 */
export const createMission = <ThrowOnError extends boolean = false>(
  options: Options<CreateMissionData, ThrowOnError>
) =>
  (options.client ?? client).post<
    CreateMissionResponses,
    CreateMissionErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Returns all missions for the given tenant with their associated runs.
 */
export const listMissionsExpanded = <ThrowOnError extends boolean = false>(
  options: Options<ListMissionsExpandedData, ThrowOnError>
) =>
  (options.client ?? client).get<
    ListMissionsExpandedResponses,
    ListMissionsExpandedErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions/expanded',
    ...options,
  });

/**
 * Deletes the specified mission including all associated data.
 */
export const deleteMission = <ThrowOnError extends boolean = false>(
  options: Options<DeleteMissionData, ThrowOnError>
) =>
  (options.client ?? client).delete<
    DeleteMissionResponses,
    DeleteMissionErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions/{missionTrn}',
    ...options,
  });

/**
 * Returns a specific mission by its TRN.
 */
export const getMission = <ThrowOnError extends boolean = false>(
  options: Options<GetMissionData, ThrowOnError>
) =>
  (options.client ?? client).get<
    GetMissionResponses,
    GetMissionErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions/{missionTrn}',
    ...options,
  });

/**
 * Updates (patches) the mission's Name.
 */
export const patchMission = <ThrowOnError extends boolean = false>(
  options: Options<PatchMissionData, ThrowOnError>
) =>
  (options.client ?? client).patch<
    PatchMissionResponses,
    PatchMissionErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions/{missionTrn}',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Marks a mission upload as complete. Triggers alignment ingestion and updates the mission with the alignment TRN.
 */
export const completeMission = <ThrowOnError extends boolean = false>(
  options: Options<CompleteMissionData, ThrowOnError>
) =>
  (options.client ?? client).post<
    CompleteMissionResponses,
    CompleteMissionErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions/{missionTrn}/complete',
    ...options,
  });

/**
 * Returns a list of runs for the given mission.
 */
export const listRuns = <ThrowOnError extends boolean = false>(
  options: Options<ListRunsData, ThrowOnError>
) =>
  (options.client ?? client).get<
    ListRunsResponses,
    ListRunsErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions/{missionTrn}/runs',
    ...options,
  });

/**
 * Creates a run for the given mission with external pointcloud creation.
 */
export const createRun = <ThrowOnError extends boolean = false>(
  options: Options<CreateRunData, ThrowOnError>
) =>
  (options.client ?? client).post<
    CreateRunResponses,
    CreateRunErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions/{missionTrn}/runs',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Deletes the specified run with all dependent data.
 */
export const deleteRun = <ThrowOnError extends boolean = false>(
  options: Options<DeleteRunData, ThrowOnError>
) =>
  (options.client ?? client).delete<
    DeleteRunResponses,
    DeleteRunErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions/{missionTrn}/runs/{runId}',
    ...options,
  });

/**
 * Returns a specific run for the given mission.
 */
export const getRun = <ThrowOnError extends boolean = false>(
  options: Options<GetRunData, ThrowOnError>
) =>
  (options.client ?? client).get<GetRunResponses, GetRunErrors, ThrowOnError>({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions/{missionTrn}/runs/{runId}',
    ...options,
  });

/**
 * Updates (patches) the run's Name.
 */
export const patchRun = <ThrowOnError extends boolean = false>(
  options: Options<PatchRunData, ThrowOnError>
) =>
  (options.client ?? client).patch<
    PatchRunResponses,
    PatchRunErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions/{missionTrn}/runs/{runId}',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Creates multiple runs for the given mission with external pointcloud creation.
 */
export const createRuns = <ThrowOnError extends boolean = false>(
  options: Options<CreateRunsData, ThrowOnError>
) =>
  (options.client ?? client).post<
    CreateRunsResponses,
    CreateRunsErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/missions/{missionTrn}/runs/bulk',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Returns a list of user project settings for the specified tenant.
 */
export const listUserProjectSettings = <ThrowOnError extends boolean = false>(
  options: Options<ListUserProjectSettingsData, ThrowOnError>
) =>
  (options.client ?? client).get<
    ListUserProjectSettingsResponses,
    ListUserProjectSettingsErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/settings',
    ...options,
  });

/**
 * Creates a new user project setting for the specified tenant.
 */
export const createUserProjectSetting = <ThrowOnError extends boolean = false>(
  options: Options<CreateUserProjectSettingData, ThrowOnError>
) =>
  (options.client ?? client).post<
    CreateUserProjectSettingResponses,
    CreateUserProjectSettingErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/settings',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Deletes the specified user project setting by its key for the specified tenant.
 */
export const deleteUserProjectSetting = <ThrowOnError extends boolean = false>(
  options: Options<DeleteUserProjectSettingData, ThrowOnError>
) =>
  (options.client ?? client).delete<
    DeleteUserProjectSettingResponses,
    DeleteUserProjectSettingErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/settings/{key}',
    ...options,
  });

/**
 * Returns a specific user project setting by its key for the specified tenant.
 */
export const getUserProjectSetting = <ThrowOnError extends boolean = false>(
  options: Options<GetUserProjectSettingData, ThrowOnError>
) =>
  (options.client ?? client).get<
    GetUserProjectSettingResponses,
    GetUserProjectSettingErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/settings/{key}',
    ...options,
  });

/**
 * Updates an existing user project setting for the specified tenant.
 */
export const updateUserProjectSetting = <ThrowOnError extends boolean = false>(
  options: Options<UpdateUserProjectSettingData, ThrowOnError>
) =>
  (options.client ?? client).patch<
    UpdateUserProjectSettingResponses,
    UpdateUserProjectSettingErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/tenants/{tenantTrn}/settings/{key}',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Returns a list of user settings.
 */
export const listUserSettings = <ThrowOnError extends boolean = false>(
  options?: Options<ListUserSettingsData, ThrowOnError>
) =>
  (options?.client ?? client).get<
    ListUserSettingsResponses,
    ListUserSettingsErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/settings',
    ...options,
  });

/**
 * Creates a new user setting.
 */
export const createUserSetting = <ThrowOnError extends boolean = false>(
  options: Options<CreateUserSettingData, ThrowOnError>
) =>
  (options.client ?? client).post<
    CreateUserSettingResponses,
    CreateUserSettingErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/settings',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

/**
 * Deletes the specified user setting by its key.
 */
export const deleteUserSetting = <ThrowOnError extends boolean = false>(
  options: Options<DeleteUserSettingData, ThrowOnError>
) =>
  (options.client ?? client).delete<
    DeleteUserSettingResponses,
    DeleteUserSettingErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/settings/{key}',
    ...options,
  });

/**
 * Returns a specific user setting by its key.
 */
export const getUserSetting = <ThrowOnError extends boolean = false>(
  options: Options<GetUserSettingData, ThrowOnError>
) =>
  (options.client ?? client).get<
    GetUserSettingResponses,
    GetUserSettingErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/settings/{key}',
    ...options,
  });

/**
 * Updates an existing user setting.
 */
export const updateUserSetting = <ThrowOnError extends boolean = false>(
  options: Options<UpdateUserSettingData, ThrowOnError>
) =>
  (options.client ?? client).patch<
    UpdateUserSettingResponses,
    UpdateUserSettingErrors,
    ThrowOnError
  >({
    security: [{ scheme: 'bearer', type: 'http' }],
    url: '/settings/{key}',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
